/**
 * Single source of truth for site-wide identity used by SEO/AEO surfaces:
 * metadata defaults, JSON-LD structured data, robots, sitemap, and the
 * generated Open Graph image. Changing the canonical host or org facts here
 * propagates everywhere, so nothing hard-codes "orgtools.com" on its own.
 */

/** Canonical host. Production serves the apex domain directly (Route 53 ALIAS
 *  to CloudFront); www is not configured in DNS, so there is no second host to
 *  redirect to or split ranking signals with. */
export const SITE_URL = "https://orgtools.com";

export const SITE_NAME = "Orgtools";

/** The differentiated one-liner. Used as the default meta description and the
 *  Organization/SoftwareApplication description so crawlers and AI models read
 *  the same positioning everywhere. */
export const SITE_DESCRIPTION =
  "A founder-mode operating model for your people and agents. Eyes on what gets decided, hands on the calls that matter.";

/** Short brand-first title used when a page has no title of its own. */
export const SITE_DEFAULT_TITLE = "Orgtools | Keep your judgment in the loop";

/** Default Open Graph / Twitter card image. A static 1200x630 brand card lives
 *  in /public so every page exposes a stable, absolute image URL. This must be
 *  referenced explicitly on each page because Next.js shallow-merges metadata:
 *  a nested page that sets its own `openGraph` overwrites the parent's entirely,
 *  so an inherited image would otherwise be dropped. metadataBase (root layout)
 *  resolves the leading slash to the canonical host. */
export const SITE_OG_IMAGE = {
  url: "/og/orgtools-default.png",
  width: 1200,
  height: 630,
  alt: "Orgtools: Keep your judgment in the loop. A founder-mode operating model for your people and agents.",
};

export const ORG_ADDRESS = {
  streetAddress: "10768 Rocky Peak Place",
  addressLocality: "Gig Harbor",
  addressRegion: "WA",
  postalCode: "98332",
  addressCountry: "US",
} as const;

/** The company's addresses, base64-encoded so none ships whole in a page or its bundle. Render
    one with `ProtectedEmailLink` or `ProtectedEmailButton`, or decode it in the browser with
    `useProtectedEmail`; never write an address in code or a code comment. Encode a new one with
    `btoa`. */
export const CONTACT_EMAIL_BASE64 = {
  /** The founders' inbox: sales, partnerships, and Talk to a human. */
  founder: "Zm91bmRlckBvcmd0b29scy5jb20=",
  /** General product support. */
  support: "c3VwcG9ydEBvcmd0b29scy5jb20=",
  privacy: "cHJpdmFjeUBvcmd0b29scy5jb20=",
  security: "c2VjdXJpdHlAb3JndG9vbHMuY29t",
} as const;

export const FOUNDERS = [
  {
    name: "Travis Truett",
    jobTitle: "CEO & Founder",
    linkedIn: "https://www.linkedin.com/in/travistruett/",
  },
] as const;

/** Capabilities surfaced to AI crawlers via SoftwareApplication.featureList.
 *  Kept in product terms that match the platform page. */
export const FEATURE_LIST = [
  "AI-guided decision workflows with named human accountability",
  "Unified fact store fed by Slack, Zoom, and uploaded files",
  "Strategic anchors that link decisions to strategy",
  "Drift, drag, and decay signal detection",
  "Live coverage of what the fact store knows and does not know yet",
  "Decision Rooms that run a decision start to finish",
  "Auto-generated meeting briefs",
] as const;

/** Resolve a site-relative path to its absolute canonical URL. */
export function absoluteUrl(path: string): string {
  if (path.startsWith("http")) return path;
  return new URL(path, SITE_URL).toString();
}
